import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Check, CheckCircle2, ChevronDown, Lock } from "lucide-react";
import { Navbar, Footer } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { LEVELS, PROBLEMS, TOPICS } from "@/constants/categories";
import { setSeo } from "@/utils/seo";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { trackEvent as track } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PATH_OPTS = [
  { value: "Sto pensando di iniziare", key: "iniziare", desc: "Sto valutando una protesi e voglio orientarmi prima di scegliere." },
  { value: "Uso già una protesi", key: "utente", desc: "La uso già e voglio affrontare dubbi o difficoltà quotidiane." },
];
const pathFromParam = (p) => PATH_OPTS.find((o) => o.key === p)?.value || "";

const GENDER_OPTS = ["Uomo", "Donna", "Preferisco non specificarlo", "Altro / preferisco descriverlo"];
const AGE_OPTS = ["Meno di 25", "25–34", "35–44", "45–54", "55–64", "65 o più", "Preferisco non specificarlo"];
const SYSTEM_OPTS = ["Poly", "Skin", "Lace", "Base ibrida", "Non lo so", "Altro"];
const HAIRCUT_OPTS = ["Corto", "Medio", "Lungo", "Frontale scoperto", "Pettinato all'indietro", "Pettinato lateralmente", "Naturale e poco strutturato", "Non ho ancora deciso"];
const HABITS_OPTS = ["Attività fisica frequente", "Sudorazione intensa", "Lavoro all'aperto", "Uso quotidiano", "Uso occasionale", "Poco tempo per la manutenzione"];
const PRIORITY_OPTS = ["Naturalezza", "Durata", "Traspirabilità", "Facilità di manutenzione", "Tenuta", "Prezzo"];

const chipBase = "min-h-[44px] rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition-colors duration-200";
const chipOn = "border-sage bg-sage/10 text-foreground";
const chipOff = "border-border bg-card text-muted-foreground hover:border-sage/50 hover:text-foreground";

function OptionGroup({ label, options, value, onSelect, multi, testid }) {
  const isOn = (o) => (multi ? value.includes(o) : value === o);
  return (
    <div className="space-y-2.5" data-testid={testid}>
      <Label className="text-sm font-semibold text-foreground">
        {label} <span className="font-normal text-muted-foreground">(facoltativo{multi ? ", più scelte" : ""})</span>
      </Label>
      <div className="grid grid-cols-2 gap-2.5">
        {options.map((o) => (
          <button
            key={o}
            type="button"
            onClick={() => onSelect(o)}
            aria-pressed={isOn(o)}
            data-testid={`${testid}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`${chipBase} ${isOn(o) ? chipOn : chipOff}`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Domanda() {
  const [params] = useSearchParams();
  const preProblem = params.get("problema");
  const [form, setForm] = useState({
    name: "",
    email: "",
    path: pathFromParam(params.get("percorso")),
    problem: PROBLEMS.includes(preProblem) || TOPICS.includes(preProblem) ? preProblem : "",
    question: "",
    gender: "",
    ageRange: "",
    systemUsed: "",
    haircut: "",
    habits: [],
    priorities: [],
    privacy: false,
    newsletter: false,
    website: "",
  });
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleMulti = (k, v) =>
    setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));
  const [showOptional, setShowOptional] = useState(false);
  const [formToken, setFormToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    setSeo(
      "Consiglio gratuito e riservato sulla tua protesi | HairHelp",
      "Raccontaci il tuo dubbio in modo riservato: un primo orientamento gratuito sulla tua protesi capillare o sui primi passi. Nessun obbligo, nessuna diagnosi medica."
    );
    track("form_open");
    axios
      .get(`${API}/questions/session`)
      .then((r) => setFormToken(r.data.token))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.path) {
      setError("Seleziona il tuo percorso: “Sto pensando di iniziare” oppure “Uso già una protesi”.");
      return;
    }
    if (!form.question.trim()) {
      setError("Scrivi la tua domanda o il tuo dubbio.");
      return;
    }
    if (!form.privacy) {
      setError("Per poterti rispondere è necessario accettare il trattamento dei dati.");
      return;
    }
    setLoading(true);
    const extra = [`Percorso: ${form.path}`];
    if (form.gender) extra.push(`Come si identifica: ${form.gender}`);
    if (form.ageRange) extra.push(`Fascia d'età: ${form.ageRange}`);
    if (form.systemUsed) extra.push(`Sistema utilizzato/valutato: ${form.systemUsed}`);
    if (form.haircut) extra.push(`Taglio/acconciatura: ${form.haircut}`);
    if (form.habits.length) extra.push(`Abitudini: ${form.habits.join(", ")}`);
    if (form.priorities.length) extra.push(`Priorità: ${form.priorities.join(", ")}`);
    const composedQuestion = `${form.question}\n\n— Informazioni aggiuntive —\n${extra.join("\n")}`;
    const level = form.problem
      ? LEVELS.find((l) => l.problems.includes(form.problem))?.id || "intermedio"
      : form.path === "Sto pensando di iniziare"
      ? "basic"
      : "intermedio";
    try {
      await axios.post(`${API}/questions`, {
        name: form.name,
        email: form.email,
        level,
        problem: form.problem || "Altro",
        question: composedQuestion,
        privacy_consent: form.privacy,
        newsletter_consent: form.newsletter,
        website: form.website,
        form_token: formToken,
      });
      track("form_submit_success", { path: form.path });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      track("form_submit_error");
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory">
      <Navbar />
      <section className="mx-auto max-w-2xl px-6 py-14 md:py-20">
        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="py-16 text-center"
            data-testid="success-message"
          >
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sage/10 text-sage">
              <CheckCircle2 className="h-8 w-8" />
            </span>
            <h1 className="mt-6 font-editorial text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">Grazie, abbiamo ricevuto la tua richiesta.</h1>
            <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-muted-foreground">
              Ti risponderemo in privato su <span className="font-semibold text-foreground">{form.email}</span>,
              normalmente entro 12–24 ore. Nessun obbligo, nessuna vendita.
            </p>
            <Link
              to="/"
              data-testid="success-home-link"
              className="mt-8 inline-block rounded-full bg-sage px-6 py-3 text-sm font-bold uppercase tracking-wide text-white transition-transform duration-300 ease-out hover:scale-105 active:scale-95"
            >
              Torna alla home
            </Link>
          </motion.div>
        ) : (
          <>
            {/* Intro */}
            <div className="text-center" data-testid="ask-intro">
              <h1 className="font-editorial text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl" data-testid="ask-title">
                Raccontaci il tuo dubbio, ti rispondiamo noi.
              </h1>
              <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg" data-testid="ask-subtitle">
                Un primo orientamento gratuito e riservato sulla tua protesi capillare o sui primi passi, basato
                sull'esperienza reale di chi vive questi sistemi ogni giorno.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2" data-testid="ask-reassure">
                {["Gratuito", "Riservato", "Senza obblighi commerciali"].map((t) => (
                  <span key={t} className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <Check className="h-4 w-4 text-sage" strokeWidth={2.5} /> {t}
                  </span>
                ))}
              </div>
            </div>

            {/* FORM */}
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
              onSubmit={submit}
              className="mt-8 space-y-6 rounded-3xl border border-border bg-card p-6 shadow-soft md:p-8"
              data-testid="question-form"
            >
              <div className="space-y-2">
                <Label htmlFor="name">Come ti chiami?</Label>
                <Input
                  id="name"
                  data-testid="name-input"
                  placeholder="Il tuo nome (facoltativo)"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="h-12 rounded-xl px-4 focus-visible:ring-sage"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">La tua email *</Label>
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  style={{ position: "absolute", width: "1px", height: "1px", padding: 0, margin: "-1px", overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}
                  data-testid="hp-website"
                />
                <Input
                  id="email"
                  type="email"
                  data-testid="email-input"
                  placeholder="Dove possiamo risponderti"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="h-12 rounded-xl px-4 focus-visible:ring-sage"
                />
                <p className="text-xs text-muted-foreground">La useremo solo per inviarti la risposta.</p>
              </div>

              <div className="space-y-3">
                <Label className="text-base font-semibold">Qual è la tua situazione? *</Label>
                <div className="grid gap-3 sm:grid-cols-2" data-testid="path-choice">
                  {PATH_OPTS.map((o) => (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => { setField("path", o.value); track("path_select", { path: o.key }); }}
                      aria-pressed={form.path === o.value}
                      data-testid={`path-option-${o.key}`}
                      className={`rounded-2xl border p-4 text-left transition-colors duration-200 ${
                        form.path === o.value ? "border-sage bg-sage/10" : "border-border bg-card hover:border-sage/50"
                      }`}
                    >
                      <span className="block text-sm font-bold text-foreground">{o.value}</span>
                      <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{o.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="question" className="text-base font-semibold">Spiega in cosa possiamo aiutarti *</Label>
                <Textarea
                  id="question"
                  data-testid="question-textarea"
                  placeholder="Puoi scriverci con parole semplici, senza usare termini tecnici."
                  value={form.question}
                  onChange={(e) => setForm({ ...form, question: e.target.value })}
                  rows={6}
                  className="min-h-[140px] rounded-xl px-4 py-3 focus-visible:ring-sage"
                />
                <p className="text-xs text-muted-foreground">
                  Più dettagli utili ci fornisci, più precisa potrà essere la risposta. Evita informazioni personali non necessarie.
                </p>
              </div>

              {/* Sezione facoltativa richiudibile */}
              <div className="rounded-2xl border border-line bg-beige/40" data-testid="optional-fields">
                <button
                  type="button"
                  onClick={() => setShowOptional((v) => { if (!v) track("form_optional_open"); return !v; })}
                  aria-expanded={showOptional}
                  data-testid="toggle-optional"
                  className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                >
                  <span className="text-sm font-medium text-foreground">
                    Se vuoi, aggiungi qualche informazione per aiutarci a comprendere meglio la situazione.
                  </span>
                  <ChevronDown className={`h-5 w-5 shrink-0 text-sage transition-transform duration-300 ${showOptional ? "rotate-180" : ""}`} />
                </button>
                {showOptional && (
                  <div className="space-y-5 border-t border-line px-5 py-5" data-testid="optional-body">
                    <div className="space-y-2.5">
                      <Label className="text-sm font-semibold text-foreground">
                        Argomento principale <span className="font-normal text-muted-foreground">(facoltativo)</span>
                      </Label>
                      <div className="grid grid-cols-2 gap-2.5" data-testid="topic-grid">
                        {TOPICS.map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setField("problem", form.problem === t ? "" : t)}
                            aria-pressed={form.problem === t}
                            data-testid={`topic-option-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className={`${chipBase} ${form.problem === t ? chipOn : chipOff}`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                    <OptionGroup label="Come ti identifichi?" testid="field-gender" options={GENDER_OPTS} value={form.gender} onSelect={(v) => setField("gender", form.gender === v ? "" : v)} />
                    <OptionGroup label="Fascia d'età" testid="field-age" options={AGE_OPTS} value={form.ageRange} onSelect={(v) => setField("ageRange", form.ageRange === v ? "" : v)} />
                    <OptionGroup label="Sistema utilizzato o valutato" testid="field-system" options={SYSTEM_OPTS} value={form.systemUsed} onSelect={(v) => setField("systemUsed", form.systemUsed === v ? "" : v)} />
                    <OptionGroup label="Taglio o acconciatura preferita" testid="field-haircut" options={HAIRCUT_OPTS} value={form.haircut} onSelect={(v) => setField("haircut", form.haircut === v ? "" : v)} />
                    <OptionGroup label="Abitudini" testid="field-habits" options={HABITS_OPTS} value={form.habits} multi onSelect={(v) => toggleMulti("habits", v)} />
                    <OptionGroup label="Priorità" testid="field-priorities" options={PRIORITY_OPTS} value={form.priorities} multi onSelect={(v) => toggleMulti("priorities", v)} />
                  </div>
                )}
              </div>

              {/* Consensi separati */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="privacy"
                  data-testid="privacy-checkbox"
                  checked={form.privacy}
                  onCheckedChange={(v) => setForm({ ...form, privacy: v === true })}
                  className="mt-0.5"
                />
                <Label htmlFor="privacy" className="cursor-pointer py-1.5 text-sm font-normal leading-relaxed text-muted-foreground">
                  Acconsento al trattamento dei miei dati per ricevere una risposta alla mia richiesta, secondo la{" "}
                  <Link to="/privacy" className="font-semibold text-sage underline">Privacy Policy</Link>. <span className="font-semibold text-foreground">(necessario)</span>
                </Label>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="newsletter"
                  data-testid="newsletter-checkbox"
                  checked={form.newsletter}
                  onCheckedChange={(v) => setForm({ ...form, newsletter: v === true })}
                  className="mt-0.5"
                />
                <Label htmlFor="newsletter" className="cursor-pointer py-1.5 text-sm font-normal leading-relaxed text-muted-foreground">
                  Vorrei ricevere in futuro guide e aggiornamenti da HairHelp. <span className="text-muted-foreground/70">(facoltativo, puoi inviare la richiesta anche senza)</span>
                </Label>
              </div>

              {error && (
                <p className="rounded-xl bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive" data-testid="form-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="submit-question-button"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-sage px-7 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-soft transition-transform duration-300 ease-out hover:scale-[1.02] active:scale-95 disabled:opacity-50"
              >
                {loading ? "Invio in corso…" : "Raccontaci il tuo dubbio in modo riservato"}
                <ArrowRight className="h-4 w-4" />
              </button>
              <p className="text-center text-xs leading-relaxed text-muted-foreground" data-testid="submit-reassurance">
                Gratuito e riservato · Nessun obbligo commerciale · Non è una diagnosi medica · Risposta entro 12–24 ore.
              </p>
            </motion.form>

            {/* Dopo il modulo: come funziona + perimetro del servizio */}
            <div className="mt-14 space-y-4" data-testid="ask-after">
              <div className="flex items-center gap-2 rounded-xl bg-muted/50 px-4 py-3" data-testid="form-privacy-note">
                <Lock className="h-4 w-4 shrink-0 text-sage" />
                <p className="text-sm font-semibold text-foreground">La tua richiesta resta privata.</p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-line bg-ivory/70 p-5" data-testid="side-after">
                  <h2 className="font-heading text-lg font-bold text-foreground">Cosa succede dopo l&rsquo;invio</h2>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    Leggiamo la tua richiesta con attenzione e ti rispondiamo in modo concreto, normalmente entro 12&ndash;24 ore.
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    Non riceverai proposte commerciali automatiche e non ti chiederemo di acquistare una protesi.
                  </p>
                </div>
                <div className="rounded-2xl border border-line bg-ivory/70 p-5" data-testid="side-help">
                  <h2 className="font-heading text-lg font-bold text-foreground">Possiamo aiutarti su</h2>
                  <ul className="mt-3 space-y-2">
                    {["scelta tra basi e materiali", "manutenzione e pulizia", "adesivi e applicazione", "taglio e personalizzazione", "sport e sudorazione", "durata e costi", "problemi durante l'utilizzo"].map((x) => (
                      <li key={x} className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground/80">
                        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-sage" /> {x}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <figure className="overflow-hidden rounded-2xl border border-line" data-testid="ask-photos">
                <img
                  src="/ask-detail.jpg"
                  alt="Mani al lavoro con strumenti e materiali di un sistema capillare"
                  className="aspect-[16/9] w-full object-cover"
                  loading="lazy"
                />
                <figcaption className="bg-beige px-4 py-2.5 text-[11px] leading-relaxed text-muted-foreground">
                  Immagine illustrativa dell&rsquo;approccio HairHelp.
                </figcaption>
              </figure>

              <div className="rounded-2xl border border-line bg-beige/40 p-5" data-testid="ask-scope">
                <h2 className="font-heading text-lg font-bold text-foreground">Cosa offre (e cosa non offre) il consiglio</h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Offriamo un primo orientamento generale, basato sull'esperienza pratica e sulle informazioni che ci
                  fornisci. Non effettuiamo diagnosi e non sostituiamo il parere di un medico o di un professionista
                  qualificato. Non possiamo garantire l'individuazione della soluzione adatta a ogni caso e non c'è alcun
                  obbligo commerciale.
                </p>
              </div>
            </div>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}
